import { ArtificialMetric, Metric, MetricsByProjectId } from 'modules/billing/types';
import { MetricProject, OrgMetric, OrgProjectMetric } from 'modules/domains/billing';
import { ColumnData } from 'ui/tableHeader';
import { Row } from '../components/SimpleIndexPage/types';
import {
  COLUMN_DEFAULTS,
  LABELS_BY_METRIC_NAME,
  PROJECT_BREAKDOWN_BREADCRUMB,
  PROJECT_BREAKDOWN_COLUMN_ORDERING,
  PROJECT_COLUMN,
  PROJECT_NAME_COLUMN_LABEL,
  PROJECT_NAME_COLUMN_VALUE,
} from '../constants';

/**
 * Deriving table columns from metrics
 */
export const createMetricColumns = (metrics: Metric[] = []) =>
  metrics.map((metric) => ({
    value: metric.metricName,
    name: LABELS_BY_METRIC_NAME[metric.metricName as keyof typeof LABELS_BY_METRIC_NAME],
    ...COLUMN_DEFAULTS,
  }));

export const sortByColumnOrder = (
  metricA: Metric | ArtificialMetric,
  metricB: Metric | ArtificialMetric,
) =>
  PROJECT_BREAKDOWN_COLUMN_ORDERING[metricA.metricName] -
  PROJECT_BREAKDOWN_COLUMN_ORDERING[metricB.metricName];

export const createColumns = (metricsByProjectId: MetricsByProjectId) => {
  const sampleMetrics = Object.values(metricsByProjectId)[0]?.sort(sortByColumnOrder);
  const metricColumns = createMetricColumns(sampleMetrics);
  return [PROJECT_COLUMN, ...metricColumns] as ColumnData[];
};

export const mapValuesToMetricNames = (metrics: (Metric | ArtificialMetric)[]) => {
  let initialMetric = {};
  if (!metrics.length) return {};
  if ((metrics[0] as Metric).project) {
    /**
     * If the metric is a Project metric, set the projectName, which will be
     * the value to filter on.
     */
    const projectName = (metrics[0] as Metric).project?.name;
    initialMetric = { projectName };
  } else {
    /**
     * If the metric is an Organization metric, projectName is set to empty
     * string so that
     */
    initialMetric = { projectName: '' };
  }

  return metrics.reduce(
    (row, { metricName, value }) => ({
      ...row,
      [metricName]: value,
    }),
    initialMetric,
  );
};

/**
 * Deriving table rows from metrics
 */
export const createRows = (
  orgMetrics: (Metric | ArtificialMetric)[],
  metricsByProjectId: MetricsByProjectId,
) => {
  if (!Object.keys(metricsByProjectId).length) return [];

  const orgRow = {
    id: 'organization',
    projectId: '',
    pinned: true,
    ...mapValuesToMetricNames(orgMetrics),
  };

  return Object.entries(metricsByProjectId).reduce(
    (rows, [projectId, metrics]) => {
      const row = {
        id: projectId,
        projectId,
        ...mapValuesToMetricNames(metrics),
        // Will output fields mapped to metrics, ie.:
        // EmailsSent: 105,
        // InAppNotificationsSent: 2050,
      };
      return [...rows, row];
    },
    [orgRow as Row],
  );
};

export const mapMetricToOption = (metric: string) => ({
  id: metric,
  label: LABELS_BY_METRIC_NAME[metric as keyof typeof LABELS_BY_METRIC_NAME],
  isSelectMenu: true,
  hasFilter: true,
  hasCountBadge: true,
});

export const mapTextToOptionTitle = (text: string) => ({
  id: text,
  label: text,
  isTitle: true,
});

export const queryByFn = (query: string, row: Row) => {
  if (query.length === 0) return true;
  return (row[PROJECT_NAME_COLUMN_VALUE] as string).toLowerCase().includes(query.toLowerCase());
};

export const createProjectBreakdownColumns = (metricNames: string[]) => {
  const columns: ColumnData[] = [
    { align: 'flex-start', value: PROJECT_NAME_COLUMN_VALUE, name: PROJECT_NAME_COLUMN_LABEL },
    ...(metricNames.map((column) => ({
      align: 'center',
      name: LABELS_BY_METRIC_NAME[column as keyof typeof LABELS_BY_METRIC_NAME],
      sortable: true,
      value: column,
    })) as ColumnData[]),
  ];
  return columns;
};

export const createProjectBreakdownRows = ({
  metricNames,
  orgMetricsByName = {},
  projects,
  metrics,
}: {
  metricNames: string[];
  orgMetricsByName: { [metricName: string]: OrgMetric };
  projects: { [projectId: string]: MetricProject };
  metrics: { [projectId: string]: OrgProjectMetric[] };
}) => {
  const projectIds = Object.keys(projects);
  const projectRows: Row[] = projectIds.map((id) => {
    const currentMetrics = metrics[id] || [];
    const metricValues = metricNames.reduce((acc, name) => {
      const value = currentMetrics.find((metric) => metric.metricName === name)?.value || 0;
      return { ...acc, [name]: value };
    }, {});

    return {
      id: id,
      projectId: id,
      projectName: projects[id].name,
      ...metricValues,
    };
  });

  const orgMetrics = metricNames.reduce((acc, curr) => {
    const currentValue = orgMetricsByName?.[curr]?.value || 0;
    return { ...acc, [curr]: currentValue };
  }, {});

  const orgRow: Row = {
    id: 'organization',
    projectId: '',
    pinned: true,
    ...orgMetrics,
  };

  return [orgRow, ...projectRows];
};

export const createBreadcrumb = (search = '') => {
  const searchParams = search.startsWith('?') ? search : '';
  const { content, href } = PROJECT_BREAKDOWN_BREADCRUMB;
  return {
    content,
    href: `${href}${searchParams}`,
  };
};
